import { useAppNotificationManager } from "@/components/AppProvider/AppNotificationProvider";
import { useDeleteReferredFrom } from "@/domain/ReferredFrom";
import { useDeleteTadSheetReferredFrom } from "@/domain/TadSheetReferredFrom";
import { translation } from "@/i18n";
import { nsSheet } from "@/i18n/locales/sheet";
import React, { useRef } from "react";
import { useNavigate } from "react-router";
import { useAppRouteParams } from "../../../../../AppRoutes";
import {
  Sheet,
  useRecursiveDeleteSheet,
  useUpdateSheet,
} from "../../../../../domain/Sheet";
import { DialogPhaseStatesType, useDialog } from "../../../helpers/hooks";
import { ReferredFromState } from "../../helper";
import { deleteRecursiveSheetData } from "../helper";

const useTranslation = translation.use(nsSheet);

export const DialogSheetDelete: React.FC<
  {
    sheets: Sheet[];
    selectedSheetIds: string[];
    setSelectedSheetIds: React.Dispatch<React.SetStateAction<string[]>>;
    isIncludingReferencedSheets: ReferredFromState;
  } & DialogPhaseStatesType
> = ({
  sheets,
  selectedSheetIds,
  setSelectedSheetIds,
  isIncludingReferencedSheets,
  dialogPhase,
  setDialogPhase,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { organizationId, versionId, sheetId } = useAppRouteParams();
  const recursiveDeleteSheet = useRecursiveDeleteSheet();
  const deleteReferredFrom = useDeleteReferredFrom();
  const deleteTadSheetReferredFrom = useDeleteTadSheetReferredFrom();
  const updateSheet = useUpdateSheet();
  const { renderDialogs } = useDialog({
    dialogPhase,
    setDialogPhase,
  });

  const { showSuccessNotification, showErrorNotification } =
    useAppNotificationManager();

  const runDelete = async () => {
    if (selectedSheetIds.includes(sheetId)) {
      navigate(`/organizations/${organizationId}/versions/${versionId}`);
    }
    setDialogPhase("in_progress");
    try {
      await deleteRecursiveSheetData({
        sheets,
        selectedSheetIds,
        recursiveDeleteSheet,
        deleteReferredFrom,
        deleteTadSheetReferredFrom,
        organizationId,
        versionId,
        updateSheet,
        setProgressCount: () => {},
      });
      showSuccessNotification(t.sheet.deleteDialog.success());
      setSelectedSheetIds([]);
      setDialogPhase(null);
    } catch (error) {
      showErrorNotification(t.sheet.deleteDialog.error(), error);
      setDialogPhase(null);
    }
  };

  // 削除を実行すると Firestore の更新が先に反映されて sheets から対象が消えるため、
  // 確認時点の対象を保持し、ダイアログを閉じ切るまで同じ内容を表示し続ける
  const confirmedTargetRef = useRef({
    displayMessage: t.sheet.deleteDialog.message({
      sheetName: t.sheet.fallbackName(),
    }),
    additionalElement: undefined as React.ReactElement | undefined,
  });
  if (dialogPhase === "confirm") {
    const selectedSheet = sheets.find(({ id }) => id === selectedSheetIds[0]);
    const displayMessage = t.sheet.deleteDialog.message({
      sheetName: selectedSheet?.displayName || t.sheet.fallbackName(),
    });

    const additionalElement =
      isIncludingReferencedSheets === "including" ? (
        <div
          style={{
            color: "var(--figma-color-status-error)",
            marginTop: 8,
          }}
        >
          {t.sheet.deleteDialog.referencedWarning()}
        </div>
      ) : undefined;

    confirmedTargetRef.current = {
      displayMessage,
      additionalElement,
    };
  }
  const confirmedTarget = confirmedTargetRef.current;

  // Don't render dialog at all if not in an active state
  if (
    !dialogPhase ||
    (dialogPhase !== "confirm" && dialogPhase !== "in_progress")
  ) {
    return null;
  }

  return (
    <>
      {renderDialogs({
        confirm: {
          title: t.sheet.deleteDialog.title(),
          displayMessage: confirmedTarget.displayMessage,
          onClick: runDelete,
          onClickCancel: () => {
            setSelectedSheetIds([]);
          },
          isThemeRed: true,
          element: confirmedTarget.additionalElement,
          buttonText: t.sheet.deleteDialog.button(),
          inProgressButtonText: t.sheet.deleteDialog.inProgressButton(),
        },
      })}
    </>
  );
};
